import React from 'react';
import { Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import './SharedActionBar.css';

export type ActionBarMode = 'browse' | 'add' | 'edit';

export interface ApprovalState {
  statusCode: string;
  isApproved: boolean;
  isPending: boolean;
  isLocked: boolean;
}

export interface SharedActionBarProps {
  mode?: ActionBarMode;
  isEditing?: boolean;
  hasRecord?: boolean;

  currentIdx?: number;
  recordCount?: number;
  onNavFirst?: () => void;
  onNavPrev?: () => void;
  onNavNext?: () => void;
  onNavLast?: () => void;

  onAdd?: () => void;
  onModify?: () => void;
  onDelete?: () => void;
  onSave?: () => void;
  onCancel?: () => void;

  approval?: ApprovalState;
  onSubmit?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  onWriteoff?: () => void;
  onHistory?: () => void;

  onPrint?: () => void;
  onCopy?: () => void;

  extraEditButtons?: React.ReactNode;
  extraBrowseButtons?: React.ReactNode;
  onExit: () => void;

  canView?: boolean;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canApprove?: boolean;
  canPrint?: boolean;

  statusLabel?: string | null;
  acceptanceActionIds?: {
    create?: string;
    update?: string;
    delete?: string;
    get?: string;
    list?: string;
    submit?: string;
    approve?: string;
    reject?: string;
    writeoff?: string;
  };
}

const EMPTY_APPROVAL_STATE: ApprovalState = {
  statusCode: '',
  isApproved: false,
  isPending: false,
  isLocked: false,
};

const PermBtn: React.FC<{
  allowed: boolean;
  disabled: boolean;
  className?: string;
  onClick?: () => void;
  acceptanceActionId?: string;
  children: React.ReactNode;
}> = ({ allowed, disabled, className, onClick, acceptanceActionId, children }) => {
  const btn = (
    <button
      className={className || 'sab-btn'}
      disabled={disabled || !allowed}
      onClick={onClick}
      data-acceptance-action={acceptanceActionId}
    >
      {children}
    </button>
  );

  return !allowed ? <Tooltip title="無權限">{btn}</Tooltip> : btn;
};

function defaultStatusLabel(approval: ApprovalState): {
  text: string;
  color: string;
  bg: string;
  border: string;
} | null {
  if (approval.isApproved) {
    return { text: '已核准', color: '#389e0d', bg: '#f6ffed', border: '#b7eb8f' };
  }
  if (approval.statusCode === 'R') {
    return { text: '已退回', color: '#cf1322', bg: '#fff1f0', border: '#ffa39e' };
  }
  if (approval.isPending) {
    return { text: '待審核', color: '#d48806', bg: '#fffbe6', border: '#ffe58f' };
  }
  return null;
}

const SharedActionBar: React.FC<SharedActionBarProps> = ({
  mode = 'browse',
  isEditing = false,
  hasRecord = false,
  currentIdx = 0,
  recordCount = 0,
  onNavFirst,
  onNavPrev,
  onNavNext,
  onNavLast,
  onAdd,
  onModify,
  onDelete,
  onSave,
  onCancel,
  approval = EMPTY_APPROVAL_STATE,
  onSubmit,
  onApprove,
  onReject,
  onWriteoff,
  onHistory,
  onPrint,
  onCopy,
  extraEditButtons,
  extraBrowseButtons,
  onExit,
  canView = true,
  canCreate = true,
  canEdit = true,
  canDelete = true,
  canApprove = true,
  canPrint = true,
  statusLabel,
  acceptanceActionIds,
}) => {
  const { isApproved, isPending, isLocked } = approval;

  const canDoSubmit = !isEditing && hasRecord && !isApproved && !isPending;
  const canDoApproval = !isEditing && hasRecord && isPending;
  const canSaveBtn = mode === 'add' ? canCreate : canEdit;

  const hasNavigation =
    !!onNavFirst || !!onNavPrev || !!onNavNext || !!onNavLast;
  const hasBrowseCrud = !!onAdd || !!onModify || !!onDelete;
  const hasEditCrud =
    !!onSave || !!onCancel || !!onAdd || !!onModify || !!extraEditButtons;
  const hasUtilities = !!onCopy || !!onPrint;
  const hasApproval =
    !!onSubmit || !!onApprove || !!onReject || !!onWriteoff || !!onHistory;

  const shouldShowActionGroup = isEditing
    ? hasEditCrud
    : hasBrowseCrud || hasUtilities || hasApproval;

  const status = statusLabel !== undefined
    ? (statusLabel
      ? { text: statusLabel, color: '#595959', bg: '#fafafa', border: '#d9d9d9' }
      : null)
    : defaultStatusLabel(approval);

  return (
    <div className="shared-action-bar" data-acceptance-action={acceptanceActionIds?.list}>
      {hasNavigation && (
        <div className="sab-group">
          {onNavFirst && (
            <PermBtn
              allowed={canView}
              disabled={isEditing || currentIdx <= 0}
              onClick={onNavFirst}
              acceptanceActionId={acceptanceActionIds?.get}
            >
              第一筆
            </PermBtn>
          )}
          {onNavPrev && (
            <PermBtn
              allowed={canView}
              disabled={isEditing || currentIdx <= 0}
              onClick={onNavPrev}
              acceptanceActionId={acceptanceActionIds?.get}
            >
              上一筆
            </PermBtn>
          )}
          {onNavNext && (
            <PermBtn
              allowed={canView}
              disabled={isEditing || currentIdx >= recordCount - 1}
              onClick={onNavNext}
              acceptanceActionId={acceptanceActionIds?.get}
            >
              下一筆
            </PermBtn>
          )}
          {onNavLast && (
            <PermBtn
              allowed={canView}
              disabled={isEditing || currentIdx >= recordCount - 1}
              onClick={onNavLast}
              acceptanceActionId={acceptanceActionIds?.get}
            >
              最後筆
            </PermBtn>
          )}
        </div>
      )}

      {shouldShowActionGroup && (
        <div className="sab-group">
          {!isEditing ? (
            <>
              {onAdd && (
                <PermBtn
                  allowed={canCreate}
                  disabled={false}
                  className="sab-btn sab-btn-primary"
                  onClick={onAdd}
                  acceptanceActionId={acceptanceActionIds?.create}
                >
                  新增
                </PermBtn>
              )}
              {onModify && (
                <PermBtn
                  allowed={canEdit}
                  disabled={!hasRecord || isLocked}
                  onClick={onModify}
                  acceptanceActionId={acceptanceActionIds?.update}
                >
                  修改
                </PermBtn>
              )}
              {onDelete && (
                <PermBtn
                  allowed={canDelete}
                  disabled={!hasRecord || isLocked}
                  className="sab-btn sab-btn-danger"
                  onClick={onDelete}
                  acceptanceActionId={acceptanceActionIds?.delete}
                >
                  刪除
                </PermBtn>
              )}

              {extraBrowseButtons}

              {hasBrowseCrud && hasUtilities && <div className="sab-separator" />}

              {onCopy && (
                <PermBtn
                  allowed={canCreate}
                  disabled={!hasRecord}
                  onClick={onCopy}
                >
                  複製
                </PermBtn>
              )}
              {onPrint && (
                <PermBtn
                  allowed={canPrint}
                  disabled={!hasRecord}
                  onClick={onPrint}
                >
                  列印
                </PermBtn>
              )}

              {(hasBrowseCrud || hasUtilities) && hasApproval && (
                <div className="sab-separator" />
              )}

              {onSubmit && (
                isPending ? (
                  <button className="sab-btn sab-btn-pending" disabled>
                    <LoadingOutlined spin style={{ marginRight: 4 }} />
                    送審中
                  </button>
                ) : (
                  <PermBtn
                    allowed={canEdit}
                    disabled={!canDoSubmit}
                    onClick={onSubmit}
                    acceptanceActionId={acceptanceActionIds?.submit}
                  >
                    送審
                  </PermBtn>
                )
              )}
              {isApproved && onWriteoff ? (
                <PermBtn
                  allowed={canApprove}
                  disabled={isEditing || !hasRecord}
                  className="sab-btn sab-btn-danger"
                  onClick={onWriteoff}
                  acceptanceActionId={acceptanceActionIds?.writeoff}
                >
                  沖銷
                </PermBtn>
              ) : onApprove ? (
                <PermBtn
                  allowed={canApprove}
                  disabled={!canDoApproval}
                  className="sab-btn sab-btn-primary"
                  onClick={onApprove}
                  acceptanceActionId={acceptanceActionIds?.approve}
                >
                  核准
                </PermBtn>
              ) : null}
              {onReject && !isApproved && (
                <PermBtn
                  allowed={canApprove}
                  disabled={!canDoApproval}
                  onClick={onReject}
                  acceptanceActionId={acceptanceActionIds?.reject}
                >
                  退回
                </PermBtn>
              )}
              {onHistory && (
                <PermBtn
                  allowed={true}
                  disabled={!hasRecord}
                  onClick={onHistory}
                >
                  審核歷史
                </PermBtn>
              )}
            </>
          ) : (
            <>
              {(onSave || onAdd) && (
                <PermBtn
                  allowed={canSaveBtn}
                  disabled={false}
                  className="sab-btn sab-btn-primary"
                  onClick={onSave || onAdd}
                  acceptanceActionId={mode === 'add' ? acceptanceActionIds?.create : acceptanceActionIds?.update}
                >
                  儲存
                </PermBtn>
              )}
              {(onCancel || onModify) && (
                <button className="sab-btn" onClick={onCancel || onModify}>
                  取消
                </button>
              )}
              {extraEditButtons}
            </>
          )}

          <div className="sab-separator" />
          <button className="sab-btn" onClick={onExit}>
            離開
          </button>
        </div>
      )}

      {!shouldShowActionGroup && (
        <div className="sab-group">
          <button className="sab-btn" onClick={onExit}>
            離開
          </button>
        </div>
      )}

      {status && (
        <span
          className="sab-status"
          style={{
            color: status.color,
            background: status.bg,
            border: `1px solid ${status.border}`,
          }}
        >
          {status.text}
        </span>
      )}
    </div>
  );
};

export default React.memo(SharedActionBar);
